import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../lib/apiClient";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from "recharts";

interface AnalyticsEntry {
  id: string;
  widgetId: string;
  timeframe: string;
  capturedAt: number;
}

interface AnalyticsOverviewResponse {
  analytics: AnalyticsEntry[];
}

export const AnalyticsPage = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["analytics", "overview"],
    queryFn: async () => {
      const response = await apiClient.get<AnalyticsOverviewResponse>("/analytics/overview");
      return response.data.analytics;
    },
    refetchInterval: 30_000
  });

  const chartData = useMemo(() => {
    const entries = data ?? [];
    const grouped = new Map<string, number>();
    entries.forEach((entry) => {
      const key = `${entry.widgetId}:${entry.timeframe}`;
      grouped.set(key, (grouped.get(key) ?? 0) + 1);
    });
    return Array.from(grouped.entries()).map(([key, count]) => {
      const [widgetId, timeframe] = key.split(":");
      return {
        widgetId,
        timeframe,
        count
      };
    });
  }, [data]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-100">Analytics Intelligence</h2>
        <p className="text-sm text-slate-400">
          Visualize real-time widget impact and conversation momentum powered by Cloudflare Durable Objects.
        </p>
      </div>

      <Card>
        <CardHeader>
          <p className="text-sm font-medium text-slate-200">Widget throughput</p>
          <p className="text-xs text-slate-500">
            Aggregated events grouped by widget and hour. Useful for capacity planning and SLA monitoring.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : chartData.length === 0 ? (
            <p className="text-sm text-slate-400">No analytics captured yet.</p>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 16, right: 24, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis
                    dataKey="timeframe"
                    stroke="#94a3b8"
                    tickFormatter={(value) => value.slice(-2)}
                    fontSize={12}
                  />
                  <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#020617", borderColor: "#1e293b" }}
                    labelStyle={{ color: "#94a3b8" }}
                  />
                  <Line type="monotone" dataKey="count" stroke="#2563eb" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
